import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, Loader2, X } from "lucide-react";
import { Eyebrow, FadeIn } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = [
  { src: "/images/sunset-bay.jpg", alt: "Sunset over the islands of Clew Bay", span: "col-span-2 row-span-2" },
  { src: "/images/toms-bar.jpg", alt: "Guests enjoying Tom's Bar on Collanmore Island", span: "row-span-2" },
  { src: "/images/group-trip.webp", alt: "A group of friends kayaking in Clew Bay", span: "" },
  { src: "/images/girl-kayak.webp", alt: "All smiles on a Quassou kayak at the shore of Clew Bay", span: "" },
  { src: "/images/sup-boards-aerial.webp", alt: "SUP boards seen from above on the calm waters of Clew Bay", span: "col-span-2" },
  { src: "/images/lads-ruins.webp", alt: "Island explorers at the old ruins on Collanmore Island", span: "" },
  { src: "/images/pontoon-hen.webp", alt: "A hen party celebrating on the pontoon in Clew Bay", span: "" },
  { src: "/images/boat-trip-1.jpg", alt: "Adventure boat trip on Clew Bay", span: "col-span-2" },
  { src: "/images/bay-sunset-aerial.webp", alt: "Sunset over Collanmore Island and Clew Bay", span: "col-span-2" },
];

function GuestPhotoStrip() {
  const [photos, setPhotos] = useState([]);
  useEffect(() => {
    axios.get(`${API}/guest-photos`).then((r) => setPhotos(r.data || [])).catch(() => {});
  }, []);
  if (!photos.length) return null;
  return (
    <div className="mt-14" data-testid="guest-photos-strip">
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">Shared by our guests</p>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {photos.map((p) => (
          <figure key={p.id} className="relative aspect-square overflow-hidden border border-gold/20">
            <img
              src={p.url}
              alt={p.caption || `Snapshot from ${p.name}`}
              data-testid={`guest-photo-${p.id}`}
              className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.05]"
            />
          </figure>
        ))}
      </div>
    </div>
  );
}

function GuestPhotoForm() {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", caption: "" });
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Choose a photo first");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("photo", file);
      await axios.post(`${API}/guest-photos`, fd);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went adrift — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="guest-photo-success" className="mx-auto mt-16 max-w-xl border border-gold/25 bg-navy-900 p-8 text-center">
        <p className="font-serif text-2xl text-ivory">Thank you!</p>
        <p className="mt-2 text-sm font-light text-mist">We&rsquo;ll review your snapshot and add it to the gallery soon.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-16 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="guest-photo-open"
          className="inline-flex items-center justify-center border border-gold/50 px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy-800"
        >
          <Camera className="mr-2 h-4 w-4" /> Share your adventure snapshot
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="guest-photo-form" className="mx-auto mt-16 max-w-xl border border-gold/25 bg-navy-900 p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <p className="font-serif text-2xl text-ivory">Share your snapshot</p>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="guest-photo-close" className="text-mist transition-colors hover:text-gold">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-6 space-y-4">
        <input
          required
          placeholder="Your name"
          data-testid="guest-photo-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-white/10 bg-navy-800 px-4 py-3 text-sm text-ivory placeholder:text-mist/60 focus:border-gold focus:outline-none"
        />
        <input
          type="email"
          placeholder="Email (optional — so we can say thanks)"
          data-testid="guest-photo-email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full border border-white/10 bg-navy-800 px-4 py-3 text-sm text-ivory placeholder:text-mist/60 focus:border-gold focus:outline-none"
        />
        <input
          placeholder="Caption (optional)"
          data-testid="guest-photo-caption"
          value={form.caption}
          onChange={(e) => setForm({ ...form, caption: e.target.value })}
          className="w-full border border-white/10 bg-navy-800 px-4 py-3 text-sm text-ivory placeholder:text-mist/60 focus:border-gold focus:outline-none"
        />
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          required
          data-testid="guest-photo-file"
          onChange={(e) => setFile(e.target.files && e.target.files[0])}
          className="w-full text-sm text-mist file:mr-4 file:cursor-pointer file:border file:border-gold/40 file:bg-transparent file:px-4 file:py-2 file:text-gold"
        />
        {error && <p data-testid="guest-photo-error" className="text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="guest-photo-submit"
          className="inline-flex items-center justify-center bg-gold px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.24em] text-navy-800 transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
          {busy ? "Sending…" : "Send photo"}
        </button>
        <p className="text-xs text-mist">JPG, PNG or WebP up to 8 MB. Photos appear on the site after we review them.</p>
      </div>
    </form>
  );
}

export default function Gallery() {
  return (
    <section id="gallery" className="bg-navy-950 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Eyebrow>The Island</Eyebrow>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 max-w-xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
                A private island, <em className="italic text-gold">a world away</em>
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <p className="max-w-md text-sm font-light leading-relaxed text-mist sm:text-base">
              Collanmore Island sits in the heart of Clew Bay, beneath Croagh Patrick on the
              Wild Atlantic Way &mdash; two minutes by RIB, a world away from ordinary.
            </p>
          </FadeIn>
        </div>

        <div className="mt-16 grid auto-rows-[170px] grid-cols-2 gap-3 sm:auto-rows-[220px] sm:grid-cols-4">
          {IMAGES.map((img, i) => (
            <FadeIn key={img.src} delay={i * 0.07} className={`h-full ${img.span}`}>
              <figure className="group relative h-full w-full overflow-hidden border border-white/5">
                <img
                  src={img.src}
                  alt={img.alt}
                  data-testid={`gallery-image-${i + 1}`}
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-navy-950/20 transition-opacity duration-500 group-hover:opacity-0" />
              </figure>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.15}>
          <GuestPhotoStrip />
          <GuestPhotoForm />
        </FadeIn>
      </div>
    </section>
  );
}
